import { useTranslation } from "react-i18next";

export default function PensionPlansSection() {
  const { t } = useTranslation();

  const plans = [
    {
      badgeKey: "plans_badge1",
      badgeStyle: "bg-emerald-50 text-emerald-700",
      tierKey: "plans_tier1",
      price: "€3,000",
      returnRate: "3–5%",
      returnStyle: "text-emerald-700",
      monthly: "€90–€150",
      monthlyStyle: "bg-emerald-50",
      monthlyTextStyle: "text-emerald-700",
      cardStyle: "bg-white text-gray-900 border border-gray-100",
      featureKeys: ["plans_feat1_1", "plans_feat1_2", "plans_feat1_3", "plans_feat1_4", "plans_feat1_5"],
      btnStyle: "border border-emerald-700 text-emerald-700 hover:bg-emerald-50",
      checkColor: "text-emerald-600",
      featureTextColor: "text-gray-600",
      isLight: true,
    },
    {
      badgeKey: "plans_badge2",
      badgeStyle: "bg-amber-400 text-gray-900",
      badgeIcon: "ri-star-fill",
      tierKey: "plans_tier2",
      price: "€5,000",
      returnRate: "103%",
      returnStyle: "text-emerald-400",
      monthly: "€429–€515",
      monthlyStyle: "bg-white/10",
      monthlyTextStyle: "text-white",
      cardStyle: "bg-gray-900 text-white",
      featureKeys: ["plans_feat2_1", "plans_feat2_2", "plans_feat2_3", "plans_feat2_4", "plans_feat2_5"],
      btnStyle: "bg-white text-gray-900 hover:bg-gray-100",
      checkColor: "text-emerald-400",
      featureTextColor: "text-white/80",
      highlight: true,
    },
    {
      badgeKey: "plans_badge3",
      badgeStyle: "bg-amber-400 text-gray-900",
      tierKey: "plans_tier3",
      price: "€10,000",
      returnRate: "157–198%",
      returnStyle: "text-amber-300",
      monthly: "€1,308–€1,650",
      monthlyStyle: "bg-white/10",
      monthlyTextStyle: "text-white",
      cardStyle: "bg-emerald-800 text-white",
      featureKeys: ["plans_feat3_1", "plans_feat3_2", "plans_feat3_3", "plans_feat3_4", "plans_feat3_5"],
      btnStyle: "bg-amber-400 text-gray-900 hover:bg-amber-300",
      checkColor: "text-emerald-400",
      featureTextColor: "text-white/80",
    },
  ];

  return (
    <section id="pension-options" className="py-14 md:py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-xs font-semibold px-4 py-2 rounded-full mb-4">
            <i className="ri-file-list-3-line"></i>
            {t("plans_badge")}
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">{t("plans_title")}</h2>
          <p className="text-gray-500 text-sm md:text-base max-w-xl mx-auto">
            {t("plans_desc")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
          {plans.map((plan) => (
            <div
              key={plan.tierKey}
              className={`rounded-2xl p-6 md:p-8 flex flex-col relative overflow-hidden transition-transform hover:-translate-y-1 ${plan.cardStyle}`}
            >
              {plan.highlight && (
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-700/20 rounded-full -translate-y-8 translate-x-8"></div>
              )}
              <div className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full mb-5 self-start ${plan.badgeStyle}`}>
                {plan.badgeIcon && <i className={`${plan.badgeIcon} text-xs`}></i>}
                {t(plan.badgeKey)}
              </div>
              <div className={`text-sm font-semibold mb-1 ${plan.highlight ? "text-white/60" : plan.isLight ? "text-gray-400" : "text-white/60"}`}>
                {t(plan.tierKey)}
              </div>
              <div className="flex items-end justify-between mb-4 md:block">
                <div>
                  <div className={`text-4xl md:text-5xl font-bold mb-0.5 ${plan.highlight ? "text-white" : plan.isLight ? "text-gray-900" : "text-white"}`}>
                    {plan.price}
                  </div>
                  <div className={`text-xs mb-0 md:mb-2 ${plan.highlight ? "text-white/50" : plan.isLight ? "text-gray-400" : "text-white/50"}`}>
                    {t("plans_contract")}
                  </div>
                </div>
                <div className={`text-xl md:text-2xl font-bold md:mb-6 ${plan.returnStyle}`}>
                  {plan.returnRate}
                  <span className={`text-xs font-normal block md:inline md:ml-1 ${plan.highlight ? "text-white/50" : plan.isLight ? "text-gray-400" : "text-white/50"}`}>
                    {t("plans_per_month")}
                  </span>
                </div>
              </div>
              <div className={`text-sm mb-5 px-4 py-3 rounded-xl ${plan.monthlyStyle}`}>
                <span className={plan.highlight ? "text-white/60" : plan.isLight ? "text-gray-500" : "text-white/60"}>{t("plans_monthly")} </span>
                <strong className={plan.monthlyTextStyle}>{plan.monthly}</strong>
              </div>
              <ul className="flex flex-col gap-2.5 mb-6 flex-1">
                {plan.featureKeys.map((key) => (
                  <li key={key} className="flex items-center gap-3">
                    <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${plan.checkColor}`}>
                      <i className="ri-check-line text-sm font-bold"></i>
                    </div>
                    <span className={`text-sm ${plan.featureTextColor}`}>{t(key)}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#contact"
                className={`w-full py-3.5 rounded-full font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap text-center ${plan.btnStyle}`}
              >
                {t("plans_sign")}
              </a>
            </div>
          ))}
        </div>
        <p className="text-center text-gray-400 text-xs mt-6">
          {t("plans_disclaimer")}
        </p>
      </div>
    </section>
  );
}
